import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

export interface ViewOption {
  icon: string;
  key?: string;
  routerLink?: string | string[];
  urlMatch?: string;
  tooltip?: string;
}

@Component({
    selector: 'app-view-toggle',
    imports: [CommonModule, RouterModule, SvgIconComponent],
    templateUrl: './view-toggle.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./view-toggle.component.css']
})
export class ViewToggleComponent implements OnInit, OnDestroy {
  @Input() views: ViewOption[] = [];
  @Input() activeKey?: string;
  @Output() viewChange = new EventEmitter<string>();

  activeIndex = 0;

  private $unsubscribe = new Subject<void>();

  constructor(private router: Router) {}

  ngOnInit(): void {
    if (this.isRouteMode) {
      this.syncActive(this.router.url);
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.$unsubscribe))
        .subscribe((e: NavigationEnd) => this.syncActive(e.urlAfterRedirects));
    }
  }

  ngOnChanges(): void {
    if (!this.isRouteMode && this.activeKey != null) {
      this.activeIndex = this.views.findIndex(v => v.key === this.activeKey);
    }
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }

  get isRouteMode(): boolean {
    return this.views.some(v => v.routerLink != null);
  }

  isActive(view: ViewOption): boolean {
    return this.activeIndex === this.views.indexOf(view);
  }

  onSelect(view: ViewOption): void {
    if (!this.isRouteMode) {
      this.activeIndex = this.views.indexOf(view);
      if (view.key != null) this.viewChange.emit(view.key);
    }
  }

  private syncActive(url: string): void {
    const match = this.views.findIndex(v => v.urlMatch && url.includes(v.urlMatch));
    this.activeIndex = match !== -1 ? match : 0;
  }
}
